"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/components/providers/auth-context";
import { useAccessControl } from "@/components/providers/access-control-context";

interface RouteGuardProps {
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
}

export function RouteGuard({ children, loadingComponent }: RouteGuardProps) {
  const { userProfile, user, loading: authLoading } = useAuth();
  const { canAccess, isLoading: accessLoading } = useAccessControl();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Wait for both auth and access control to load
    if (authLoading || accessLoading) return;

    // Ignore checks for auth pages and the verification page itself
    if (pathname === "/login" || pathname === "/signup" || pathname === "/verify-email") return;

    if (!user) {
      router.push("/login");
      return;
    }

    // Primary check: If user exists but email is not verified, force to verification page
    // This handles manual signups immediately after account creation.
    if (user && !user.emailVerified) {
       // Check if the user profile indicates manual auth method, just to be safe
       // though emailVerified should only be false for manual signups initially.
       // Also ensure we are not already on the verify-email page.
       if (userProfile?.authMethod === 'manual' && pathname !== '/verify-email') {
          console.log("RouteGuard: Unverified manual user detected, forcing redirect to /verify-email");
          router.push("/verify-email");
          return;
       }
       // If it's not a manual user or somehow emailVerified is false for social (shouldn't happen),
       // or if we are already on verify-email, let other checks handle it or allow staying.
    }

    // Secondary check: Access control based on user role/settings for the specific path
    const hasAccess = canAccess(pathname);
    if (!hasAccess) {
      router.push("/login");
    }
  }, [userProfile, user, authLoading, accessLoading, canAccess, router, pathname]);

  // Show loading state while checking auth
  if (authLoading || accessLoading) {
    return loadingComponent || <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Allow access if:
  // 1. It's a login/signup page, or
  // 2. User has access per settings
  if (pathname === "/login" || pathname === "/signup") {
    return <>{children}</>;
  }

  return canAccess(pathname) ? <>{children}</> : null;
}
